import { Fragment } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { GlobalMigrateUserModal } from '../components/GlobalMigrateUserModal/GlobalMigrateUserModal';
import AuthorizedGuard from '../guards/AuthorizedGuard';
import CmsGuard from '../guards/CmsGuard';
import LazyGuard from '../guards/LazyGuard';
import { LoanDocumentGuard } from '../guards/LoanDocumentGuard';
import RequireAuth from '../guards/RequireAuth';
import RequireContext from '../guards/RequireContext';
import RouteChangeGuard from '../guards/RouteChangeGuard/RouteChangeGuard';
import SessionGuard from '../guards/SessionGuard';
import StateGuard from '../guards/StateGuard';
import UIRevampCleanupGuard from '../guards/UIRevampCleanupGuard/UIRevampCleanupGuard';
import SetupParamsGuard from '../guards/UrlParams/SetupParamsGuard';
import { UrlParamsProcessors } from '../guards/UrlParams/UrlParamsProcessors';
import WorkflowGuard from '../guards/WorkflowGuard';
import AccountDetails from '../pages/Account/Account';
import ActivationPage from '../pages/Activation';
import { ApplicationDetails } from '../pages/Applications/ApplicationDetails';
import ApplicationUpdatePage from '../pages/ApplicationUpdate';
import DashboardView from '../pages/Dashboard';
import LoanDocumentsList from '../pages/LoanDocuments/LoanDocumentsList';
import LoanDocumentsSubmitted from '../pages/LoanDocuments/LoanDocumentsSubmitted';
import LoanDocumentsUpload from '../pages/LoanDocuments/LoanDocumentsUpload';
import { MainLayout } from '../pages/MainLayout/MainLayout';
import RateUpdatePage from '../pages/RateUpdate';
import SignInView from '../pages/SignIn';
import SignInVerifyPinView from '../pages/SignInVerifyPin';
import SignOutView from '../pages/SignOut';
import { VehicleRoot } from '../pages/Vehicle/VehicleRoot';
import useApplicantStore from '../store/applicantStore/useApplicantStore';
import useDashboardStore from '../store/dashboardStore/useDashboardStore';
import useVerificationStore from '../store/verificationStore/useVerificationStore';
import { VERIFICATION_GATE } from '../workflow/assembly/DefaultVerification/gate';
import { APPLICANT_GATE } from '../workflow/assembly/UnifiedDefaultApplicant/gate';
import { CO_APPLICANT_GATE } from '../workflow/assembly/UnifiedDefaultCoApplicant/gate';
import { NO_OFFERS_GATE } from '../workflow/assembly/UnifiedDefaultNoOffers/gate';
import { OFFERS_GATE } from '../workflow/assembly/UnifiedDefaultOffers/gate';
import { REVIEW_GATE } from '../workflow/assembly/UnifiedDefaultReview/gate';
import { SIGN_UP_GATE } from '../workflow/assembly/UnifiedDefaultSignUp/gate';
import { GateRoot } from '../workflow/common/Gate/Gate';
import { WorkflowHub } from '../workflow/common/WorkflowHub';
import { NavigateWithState } from './NavigateWithState';
import { LazyRoute } from './types/LazyRoute';

const unifiedInternalRoutes = [
  {
    path: 'sign-up/*',
    element: (
      <AuthorizedGuard>
        <WorkflowHub />
      </AuthorizedGuard>
    ),
    children: [
      {
        path: '*',
        index: true,
        element: <GateRoot gate={SIGN_UP_GATE} />,
      },
    ],
  },
  {
    path: 'sign-in',
    element: (
      <AuthorizedGuard>
        <SignInView />
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-in/activation',
    element: (
      <CmsGuard>
        <AuthorizedGuard>
          <ActivationPage />
        </AuthorizedGuard>
      </CmsGuard>
    ),
  },

  {
    path: 'sign-in/rate-update',
    element: (
      <CmsGuard>
        <AuthorizedGuard>
          <RateUpdatePage />
        </AuthorizedGuard>
      </CmsGuard>
    ),
  },
  {
    path: 'sign-in/application-update',
    element: (
      <CmsGuard>
        <AuthorizedGuard>
          <ApplicationUpdatePage />
        </AuthorizedGuard>
      </CmsGuard>
    ),
  },
  {
    path: 'sign-in/verify/pin',
    element: (
      <AuthorizedGuard>
        <StateGuard>
          <SignInVerifyPinView />
        </StateGuard>
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-out',
    element: <SignOutView />,
  },
  {
    path: 'loan-documents',
    element: (
      <RequireAuth>
        <UrlParamsProcessors>
          <RequireContext>
            <Outlet />
          </RequireContext>
        </UrlParamsProcessors>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="list" />,
      },
      {
        path: 'list',
        element: <LoanDocumentsList />,
      },
      {
        path: 'retail-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'service-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'gap-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'submitted',
        element: <LoanDocumentsSubmitted />,
      },
    ],
  },
  {
    path: 'workflow',
    element: (
      <RequireAuth>
        <WorkflowHub />
      </RequireAuth>
    ),
    children: [
      {
        path: 'applicant/*',
        element: (
          <RequireContext>
            <GateRoot gate={APPLICANT_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'co-applicant/*',
        element: (
          <RequireContext>
            <GateRoot gate={CO_APPLICANT_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'offers/*',
        element: (
          <RequireContext>
            <GateRoot gate={OFFERS_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'review/*',
        element: (
          <RequireContext>
            <GateRoot gate={REVIEW_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'verification/*',
        element: (
          <RequireContext>
            <GateRoot gate={VERIFICATION_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'no-offers/*',
        element: (
          <RequireContext>
            <GateRoot gate={NO_OFFERS_GATE} />
          </RequireContext>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <RequireAuth>
        <UrlParamsProcessors>
          <MainLayout />
        </UrlParamsProcessors>
      </RequireAuth>
    ),
    children: [
      {
        path: '',
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: 'dashboard/*',
        children: [
          {
            index: true,
            element: <DashboardView />,
          },
          {
            path: 'vehicle/*',
            element: (
              <RequireContext>
                <StateGuard>
                  <VehicleRoot />
                </StateGuard>
              </RequireContext>
            ),
            children: [
              {
                path: '*',
                index: true,
                element: <NavigateWithState to="application" replace />,
              },
              {
                path: 'application/*',
                element: <ApplicationDetails />,
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to="" replace />,
          },
        ],
      },
      {
        path: 'details/*',
        element: (
          <RequireContext>
            <AccountDetails />
          </RequireContext>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="" replace />,
  },
];

export const routes = [
  {
    path: '',
    element: (
      <RouteChangeGuard>
        <SetupParamsGuard>
          <Fragment>
            <Outlet />
            <GlobalMigrateUserModal />
          </Fragment>
        </SetupParamsGuard>
      </RouteChangeGuard>
    ),
    children: [
      {
        path: 'sign-in',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute => import('../pages/SignInPage/SignInPage'),
          },
          {
            path: 'application-update',
            lazy: (): LazyRoute =>
              import('../pages/ApplicationUpdatePage/ApplicationUpdatePage'),
          },
          {
            path: 'pin-verify',
            lazy: (): LazyRoute =>
              import('../pages/SignInPinVerifyPage/SignInPinVerifyPage'),
          },
        ],
      },
      {
        path: 'sign-up',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute => import('../pages/SignUpPage/SignUpPage'),
          },
          {
            path: 'pin-verify',
            lazy: (): LazyRoute =>
              import('../pages/SignUpPinVerifyPage/SignUpPinVerifyPage'),
          },
          {
            path: 'returning-user',
            lazy: (): LazyRoute =>
              import(
                '../pages/SignUpReturningUserPage/SignUpReturningUserPage'
              ),
          },
        ],
      },
      {
        path: 'sign-out',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <LazyGuard>
              <Outlet />
            </LazyGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute => import('../pages/SignOutPage/SignOutPage'),
          },
        ],
      },
      {
        path: 'mail',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <LazyGuard>
              <Outlet />
            </LazyGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute => import('../pages/MailCodePage/MailCodePage'),
          },
          {
            path: 'offer',
            lazy: (): LazyRoute =>
              import('../pages/MailCodeOfferPage/MailCodeOfferPage'),
          },
          {
            path: 'activation',
            lazy: (): LazyRoute =>
              import('../pages/MailCodeActivationPage/MailCodeActivationPage'),
          },
          {
            path: 'pin-verify',
            lazy: (): LazyRoute =>
              import('../pages/MailCodePinVerifyPage/MailCodePinVerifyPage'),
          },
        ],
      },
      {
        path: 'activation',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute =>
              import('../pages/ActivationPage/ActivationPage'),
          },
          {
            path: 'pin-verify',
            lazy: (): LazyRoute =>
              import(
                '../pages/ActivationPinVerifyPage/ActivationPinVerifyPage'
              ),
          },
        ],
      },
      {
        path: 'dashboard',
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/garage" />,
          },
          {
            path: 'garage',
            lazy: (): LazyRoute =>
              import('../pages/DashboardGaragePage/DashboardGaragePage'),
          },
          {
            path: 'application/:vehicleId',
            lazy: (): LazyRoute =>
              import(
                '../pages/DashboardApplicationPage/DashboardApplicationPage'
              ),
          },
        ],
      },
      {
        path: 'update-vehicle/:vehicleId',
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute =>
              import(
                '../pages/VehicleConfirmationPage/VehicleConfirmationPage'
              ),
          },
        ],
      },
      {
        path: 'apply',
        element: (
          <WorkflowGuard>
            <CmsGuard>
              <SessionGuard>
                <LazyGuard>
                  <Outlet />
                </LazyGuard>
              </SessionGuard>
            </CmsGuard>
          </WorkflowGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute => import('../pages/Apply/ApplyPage/ApplyPage'),
          },
          {
            path: 'application-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplyApplicationTypePage/ApplyApplicationTypePage'
              ),
          },
          {
            path: 'refinance-goal',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplyRefinanceGoalPage/ApplyRefinanceGoalPage'
              ),
          },
          {
            path: 'purchase-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplyPurchaseTypePage/ApplyPurchaseTypePage'
              ),
          },
          {
            path: 'vehicle-condition',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplyVehicleConditionPage/ApplyVehicleConditionPage'
              ),
          },
          {
            path: 'search-vehicle',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplySearchVehiclePage/ApplySearchVehiclePage'
              ),
          },
          {
            path: 'search-vin',
            lazy: (): LazyRoute =>
              import('../pages/Apply/ApplySearchVinPage/ApplySearchVinPage'),
          },
          {
            path: 'search-plate',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplySearchPlatePage/ApplySearchPlatePage'
              ),
          },
          {
            path: 'search-year',
            lazy: (): LazyRoute =>
              import('../pages/Apply/ApplySearchYearPage/ApplySearchYearPage'),
          },
          {
            path: 'search-make',
            lazy: (): LazyRoute =>
              import('../pages/Apply/ApplySearchMakePage/ApplySearchMakePage'),
          },
          {
            path: 'search-model',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplySearchModelPage/ApplySearchModelPage'
              ),
          },
          {
            path: 'confirm-vehicle',
            lazy: (): LazyRoute =>
              import(
                '../pages/Apply/ApplyConfirmVehiclePage/ApplyConfirmVehiclePage'
              ),
          },
        ],
      },
      {
        path: 'applicant/:vehicleId',
        loader: ({ params }): Promise<Response | null> =>
          useApplicantStore.getState().loader(params),
        shouldRevalidate: ({ currentUrl, nextUrl }): boolean =>
          useDashboardStore.getState().computed.isVehicleEmpty ||
          currentUrl.pathname !== nextUrl.pathname,
        element: (
          <WorkflowGuard>
            <CmsGuard>
              <SessionGuard>
                <LazyGuard>
                  <Outlet />
                </LazyGuard>
              </SessionGuard>
            </CmsGuard>
          </WorkflowGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute =>
              import('../pages/Applicant/ApplicantPage/ApplicantPage'),
          },
          {
            path: 'residence-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantResidenceTypePage/ApplicantResidenceTypePage'
              ),
          },
          {
            path: 'residence-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantResidenceDetailsPage/ApplicantResidenceDetailsPage'
              ),
          },
          {
            path: 'residence-duration',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantResidenceDurationPage/ApplicantResidenceDurationPage'
              ),
          },
          {
            path: 'previous-residence',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantPreviousResidencePage/ApplicantPreviousResidencePage'
              ),
          },
          {
            path: 'employment-status',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEmploymentStatusPage/ApplicantEmploymentStatusPage'
              ),
          },
          {
            path: 'employment-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEmploymentTypePage/ApplicantEmploymentTypePage'
              ),
          },
          {
            path: 'unemployment-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantUnemploymentTypePage/ApplicantUnemploymentTypePage'
              ),
          },
          {
            path: 'employment-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEmploymentDetailsPage/ApplicantEmploymentDetailsPage'
              ),
          },
          {
            path: 'unemployment-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantUnemploymentDetailsPage/ApplicantUnemploymentDetailsPage'
              ),
          },
          {
            path: 'employment-duration',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEmploymentDurationPage/ApplicantEmploymentDurationPage'
              ),
          },
          {
            path: 'previous-employment',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantPreviousEmploymentPage/ApplicantPreviousEmploymentPage'
              ),
          },
          {
            path: 'additional-income',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantAdditionalIncomePage/ApplicantAdditionalIncomePage'
              ),
          },
          {
            path: 'income-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantIncomeDetailsPage/ApplicantIncomeDetailsPage'
              ),
          },
          {
            path: 'add-coapplicant',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantAddCoapplicantPage/ApplicantAddCoapplicantPage'
              ),
          },
          {
            path: 'coapp-personal-relationship',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantRelationshipPage/CoapplicantRelationshipPage'
              ),
          },
          {
            path: 'coapp-personal-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantPersonalDetailsPage/CoapplicantPersonalDetailsPage'
              ),
          },
          {
            path: 'coapp-residence-relationship',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantResidenceRelationshipPage/CoapplicantResidenceRelationshipPage'
              ),
          },
          {
            path: 'coapp-same-residence',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantSameResidencePage/CoapplicantSameResidencePage'
              ),
          },
          {
            path: 'coapp-different-residence-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantDifferentResidenceTypePage/CoapplicantDifferentResidenceTypePage'
              ),
          },
          {
            path: 'coapp-different-residence',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantDifferentResidencePage/CoapplicantDifferentResidencePage'
              ),
          },
          {
            path: 'coapp-residence-duration',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantResidenceDurationPage/CoapplicantResidenceDurationPage'
              ),
          },
          {
            path: 'coapp-previous-residence',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantPreviousResidencePage/CoapplicantPreviousResidencePage'
              ),
          },
          {
            path: 'coapp-employment-status',
            loader: (): Promise<Response | null> =>
              useApplicantStore.getState().loadApplicant(),
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentStatusPage/CoapplicantEmploymentStatusPage'
              ),
          },
          {
            path: 'coapp-employment-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentTypePage/CoapplicantEmploymentTypePage'
              ),
          },
          {
            path: 'coapp-unemployment-type',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantUnemploymentTypePage/CoapplicantUnemploymentTypePage'
              ),
          },
          {
            path: 'coapp-employment-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentDetailsPage/CoapplicantEmploymentDetailsPage'
              ),
          },
          {
            path: 'coapp-unemployment-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantUnemploymentDetailsPage/CoapplicantUnemploymentDetailsPage'
              ),
          },
          {
            path: 'coapp-employment-duration',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentDurationPage/CoapplicantEmploymentDurationPage'
              ),
          },
          {
            path: 'coapp-previous-employment',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantPreviousEmploymentPage/CoapplicantPreviousEmploymentPage'
              ),
          },
          {
            path: 'coapp-additional-income',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantAdditionalIncomePage/CoapplicantAdditionalIncomePage'
              ),
          },
          {
            path: 'coapp-income-details',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantIncomeDetailsPage/CoapplicantIncomeDetailsPage'
              ),
          },
          {
            path: 'edit-personal',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEditPersonalPage/ApplicantEditPersonalPage'
              ),
          },
          {
            path: 'coapp-edit-personal',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEditPersonalPage/CoapplicantEditPersonalPage'
              ),
          },
          {
            path: 'edit-residence',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEditResidencePage/ApplicantEditResidencePage'
              ),
          },
          {
            path: 'coapp-edit-residence',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEditResidencePage/CoapplicantEditResidencePage'
              ),
          },
          {
            path: 'edit-employment',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEditEmploymentPage/ApplicantEditEmploymentPage'
              ),
          },
          {
            path: 'coapp-edit-employment',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEditEmploymentPage/CoapplicantEditEmploymentPage'
              ),
          },
          {
            path: 'edit-income',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantEditIncomePage/ApplicantEditIncomePage'
              ),
          },
          {
            path: 'coapp-edit-income',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantEditIncomePage/CoapplicantEditIncomePage'
              ),
          },
          {
            path: 'review-applicant',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantReviewPage/ApplicantReviewPage'
              ),
          },
          {
            path: 'coapp-review-applicant',
            lazy: (): LazyRoute =>
              import(
                '../pages/Coapplicant/CoapplicantReviewPage/CoapplicantReviewPage'
              ),
          },
          {
            path: 'submit-application',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantSubmitPage/ApplicantSubmitPage'
              ),
          },
          {
            path: 'application-success',
            lazy: (): LazyRoute =>
              import(
                '../pages/Applicant/ApplicantSuccessPage/ApplicantSuccessPage'
              ),
          },
        ],
      },
      {
        path: 'loan-documents',
        element: (
          <CmsGuard>
            <SessionGuard>
              <LoanDocumentGuard>
                <LazyGuard>
                  <Outlet />
                </LazyGuard>
              </LoanDocumentGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute =>
              import('../pages/LoanDocument/LoanDocumentPage/LoanDocumentPage'),
          },
          {
            path: 'list',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentListPage/LoanDocumentListPage'
              ),
          },
          {
            path: 'ric-upload',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadRICPage/LoanDocumentUploadRICPage'
              ),
          },
          {
            path: 'vsc-upload',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadVSCPage/LoanDocumentUploadVSCPage'
              ),
          },
          {
            path: 'gap-upload',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadGAPPage/LoanDocumentUploadGAPPage'
              ),
          },
          {
            path: 'document-upload',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadAllPage/LoanDocumentUploadAllPage'
              ),
          },
          {
            path: 'current-mileage',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentMileagePage/LoanDocumentMileagePage'
              ),
          },
          {
            path: 'success',
            lazy: (): LazyRoute =>
              import(
                '../pages/LoanDocument/LoanDocumentSuccessPage/LoanDocumentSuccessPage'
              ),
          },
        ],
      },
      {
        path: 'verifications/:vehicleId',
        loader: ({ params, request }): Promise<Response | null> =>
          useVerificationStore.getState().loader(params, request),
        shouldRevalidate: ({ currentUrl, nextUrl }): boolean =>
          useDashboardStore.getState().computed.isVehicleEmpty ||
          currentUrl.pathname !== nextUrl.pathname,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute =>
              import(
                '../pages/Verifications/VerificationPage/VerificationPage'
              ),
          },
          {
            path: 'document-list',
            lazy: (): LazyRoute =>
              import(
                '../pages/Verifications/VerificationDocumentListPage/VerificationDocumentListPage'
              ),
          },
          ...[
            'upload-license',
            'coapp-upload-license',
            'upload-insurance',
            'upload-registration',
            'upload-odometer',
            'upload-income',
            'coapp-upload-income',
            'upload-additional',
          ].map((path) => ({
            path: path,
            lazy: (): LazyRoute =>
              import(
                '../pages/Verifications/VerificationUploadPage/VerificationUploadPage'
              ),
          })),
        ],
      },
      {
        path: 'not-found',
        element: (
          <LazyGuard>
            <Outlet />
          </LazyGuard>
        ),
        children: [
          {
            index: true,
            lazy: (): LazyRoute => import('../pages/NotFoundPage/NotFoundPage'),
          },
        ],
      },
      {
        path: 'unified',
        element: (
          <UIRevampCleanupGuard>
            <Outlet />
          </UIRevampCleanupGuard>
        ),
        children: unifiedInternalRoutes,
      },
      {
        path: '',
        element: <Navigate to="unified" replace />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];
